// The console's mirror of the server's rules for S10, S17 and S18 (spec 15
// §7; D-620, D-621, D-622). The server decides; this decides only what the
// console offers, so it never offers a control the server refuses. Pure.

import { PlatformError, type PlatformMe, type PlatformRole } from "./platform-api";
import type { Grant, StaffMember, StaffRole } from "./support-api";

/** S10's people list, reset link, per-user revoke and impersonation. */
export function canUseSupportTools(role: PlatformRole): boolean {
  return role === "platform_admin" || role === "platform_support";
}

/** S10's tenant-wide revoke and act_as approval: platform_admin only. */
export function canActAsAdmin(role: PlatformRole): boolean {
  return role === "platform_admin";
}

/** S18 reads: platform_admin and platform_readonly. */
export function canReadAudit(role: PlatformRole): boolean {
  return role === "platform_admin" || role === "platform_readonly";
}

/** S17 reads (the same pair as S18) and S17 writes (platform_admin). */
export function canReadStaffDirectory(role: PlatformRole): boolean {
  return canReadAudit(role);
}

export function canManageStaff(role: PlatformRole): boolean {
  return role === "platform_admin";
}

/** A grant is live while it can still be used or approved. */
export function isLiveGrant(grant: Pick<Grant, "status">): boolean {
  return grant.status === "active" || grant.status === "pending_approval";
}

/** What S10 offers on one live grant (§3.7 steps 2, 3 and 5). */
export function grantActions(
  me: Pick<PlatformMe, "id" | "role">,
  grant: Pick<Grant, "staff_id" | "status">,
): { open: boolean; approve: boolean; end: boolean } {
  const owner = grant.staff_id === me.id;
  return {
    open: owner && grant.status === "active",
    approve: me.role === "platform_admin" && grant.status === "pending_approval",
    end: (owner || me.role === "platform_admin") && isLiveGrant(grant),
  };
}

/** Whole minutes left before `expiresAt`, never below zero. */
export function minutesLeft(expiresAt: string, now: Date): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 60_000));
}

/** The impersonation modes a role may ask for (§7: billing and readonly none). */
export function grantModes(role: PlatformRole): readonly ("read_only" | "act_as")[] {
  return canUseSupportTools(role) ? ["read_only", "act_as"] : [];
}

/** True when an act_as grant by this role waits for an admin's approval. */
export function needsApproval(role: PlatformRole, mode: "read_only" | "act_as"): boolean {
  return mode === "act_as" && role !== "platform_admin";
}

export interface StaffActions {
  changeRole: boolean;
  disable: boolean;
  enable: boolean;
  resendInvite: boolean;
  resetTotp: boolean;
}

const NONE: StaffActions = {
  changeRole: false,
  disable: false,
  enable: false,
  resendInvite: false,
  resetTotp: false,
};

/**
 * What S17 offers on one member (D-621): a platform_admin acts on anyone but
 * themselves, a sandbox admin on sandbox members only (D-477 ruling 5). The
 * last active fleet admin is the server's to refuse (LAST_ADMIN), because the
 * count can change under the screen.
 */
export function staffActions(me: PlatformMe, member: StaffMember): StaffActions {
  if (!canManageStaff(me.role)) return NONE;
  if (member.id === me.id) return NONE;
  if (me.scope === "sandbox" && member.scope !== "sandbox") return NONE;
  return {
    changeRole: member.status !== "disabled",
    disable: member.status !== "disabled",
    enable: member.status === "disabled",
    resendInvite: member.status === "invited",
    resetTotp: member.status === "active",
  };
}

/** The roles a role change may pick (a sandbox admin never makes an admin). */
export function assignableRoles(me: PlatformMe): readonly StaffRole[] {
  const all: StaffRole[] = [
    "platform_admin",
    "platform_support",
    "platform_billing",
    "platform_readonly",
  ];
  return me.scope === "sandbox" ? all.filter((r) => r !== "platform_admin") : all;
}

/** Why a support or staff write was refused, for the sentence the screen shows. */
export type SupportFailure =
  | "session"
  | "scope"
  | "role"
  | "last_admin"
  | "self"
  | "state"
  | "not_eligible"
  | "confirmation"
  | "already_accepted"
  | "grant_live"
  | "not_found"
  | "failed";

export function supportFailure(error: unknown): SupportFailure {
  if (!(error instanceof PlatformError)) return "failed";
  if (error.status === 401) return "session";
  if (error.status === 404) return "not_found";
  if (error.status === 403) return error.code === "STAFF_SCOPE_DENIED" ? "scope" : "role";
  switch (error.code) {
    case "LAST_ADMIN":
      return "last_admin";
    case "STAFF_SELF_CHANGE":
      return "self";
    case "STAFF_STATE_CONFLICT":
    case "GRANT_NOT_ACTIVE":
    case "GRANT_NOT_APPROVED":
      return "state";
    case "SUPPORT_RESET_NOT_ELIGIBLE":
      return "not_eligible";
    case "CONFIRMATION_MISMATCH":
      return "confirmation";
    case "INVITE_ALREADY_ACCEPTED":
      return "already_accepted";
    case "GRANT_ALREADY_LIVE":
      return "grant_live";
    default:
      return "failed";
  }
}

/** The reason a reset link was not sent, when the server named one. */
export function notEligibleReason(error: unknown): string | undefined {
  return error instanceof PlatformError ? error.reason : undefined;
}

/** The audit row's actor, read for a person: a staff id named from the
 * directory, a system worker by its name, never a raw id. */
export type AuditActor =
  | { kind: "staff"; staffId: string }
  | { kind: "system"; worker: string }
  | { kind: "other"; raw: string };

export function auditActor(actor: string): AuditActor {
  if (actor.startsWith("staff:")) return { kind: "staff", staffId: actor.slice("staff:".length) };
  if (actor.startsWith("system:")) return { kind: "system", worker: actor.slice("system:".length) };
  return { kind: "other", raw: actor };
}

/** The local calendar day `yyyy-mm-dd` as the UTC instant of its start (the
 * date filter's `from`) or of the next day's start (its exclusive `to`). */
export function dayBoundary(day: string, edge: "from" | "to"): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(day);
  if (!match) return undefined;
  const local = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (edge === "to") local.setDate(local.getDate() + 1);
  return local.toISOString();
}
